<script setup lang="ts">
import type { SliderRootEmits, SliderRootProps } from 'reka-ui';
import { cn } from '~/lib/utils';
import { SliderRange, SliderRoot, SliderThumb, SliderTrack, useForwardPropsEmits } from 'reka-ui';
import { computed, type HTMLAttributes } from 'vue';

const props = defineProps<
  SliderRootProps & {
    class?: HTMLAttributes['class'];
    formatTooltip?: (val: number) => number | string;
  }
>();
const emits = defineEmits<SliderRootEmits>();

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props;

  return delegated;
});

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const formatTooltip = (val: number) => {
  if (props.formatTooltip) {
    return props.formatTooltip(val);
  }
  return val;
};
</script>

<template>
  <SliderRoot
    v-slot="{ modelValue }"
    data-slot="slider"
    :class="
      cn(
        'relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col z-10',
        props.class,
      )
    "
    v-bind="forwarded"
  >
    <SliderTrack
      data-slot="slider-track"
      class="bg-muted relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5"
    >
      <SliderRange
        data-slot="slider-range"
        class="bg-primary absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full"
      />
    </SliderTrack>

    <SliderThumb
      v-for="(v, key) in modelValue"
      :key="key"
      :data-slot="formatTooltip(v)"
      class="border-primary bg-background ring-ring/50 block size-4 shrink-0 rounded-full border shadow-sm transition-[color,box-shadow] focus-visible:ring-4 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 before:content-[attr(data-slot)] before:rounded before:bg-[#333] before:text-white before:opacity-0 before:whitespace-nowrap before:text-xs before:leading-4 before:px-2 before:py-1.5 before:absolute before:-translate-x-1/2 before:-translate-y-1/2 before:left-1/2 before:bottom-3 before:duration-300 before:pointer-events-none after:content-[''] after:border-6 after:border-transparent after:border-b-[#333] after:absolute after:-translate-x-1/2 after:-translate-y-1/2 after:left-1/2 after:opacity-0 after:rotate-180 after:bottom-2 after:duration-300 after:pointer-events-none hover:ring-4 hover:cursor-grab hover:before:opacity-100 hover:after:opacity-100"
    />
  </SliderRoot>
</template>
